import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

const MOAI_STANDOFF_X = GAME_WIDTH * 0.74
const MOAI_APPROACH_SPEED = 0.95
const MOAI_CHARGE_MS = 700
const MOAI_RECOVER_MS = 520

type NeonMoaiPhase = "approach" | "charging" | "firing" | "recovering"

export class NeonMoaiEnemy extends StatefulEnemyRuntime<NeonMoaiPhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<NeonMoaiPhase>[] = [
      {
        name: "approach",
        behaviorState: "tracking",
        animationState: "flying",
        canFire: false,
        update: (context) => this.updateApproach(context),
      },
      {
        name: "charging",
        behaviorState: "focusing",
        animationState: "focusing",
        canFire: false,
        enter: (context) => {
          context.sprite.setTint(0x38bdf8)
          context.vfx.emitImpactBurst(
            context.sprite.x,
            context.sprite.y,
            0x38bdf8,
            1,
          )
        },
        update: (context) => this.updateCharging(context),
      },
      {
        name: "firing",
        behaviorState: "attacking",
        animationState: "attacking",
        canFire: true,
        enter: (context) => {
          context.enemyData.lastShot = context.enemyData.shootCooldown
          context.sprite.setTint(0xf0f9ff)
        },
        update: (context) => this.updateFiring(context),
      },
      {
        name: "recovering",
        behaviorState: "retreating",
        animationState: "retreating",
        canFire: false,
        enter: (context) => {
          context.sprite.clearTint()
        },
        update: (context) => this.updateRecovering(context),
      },
    ]

    super(sprite, enemyData, "approach", states)
  }

  private updateApproach(
    context: EnemyStateContext<NeonMoaiPhase>,
  ): NeonMoaiPhase | null {
    this.applyMoaiFloat(context)
    context.enemyData.vx = context.scrollCompensation - MOAI_APPROACH_SPEED

    if (context.sprite.x <= MOAI_STANDOFF_X) {
      return "charging"
    }

    return null
  }

  private updateCharging(
    context: EnemyStateContext<NeonMoaiPhase>,
  ): NeonMoaiPhase | null {
    this.applyMoaiFloat(context)
    context.enemyData.vx = context.scrollCompensation
    context.sprite.angle = Math.sin(context.stateTime / 70) * 3

    if (context.stateTime >= MOAI_CHARGE_MS) {
      return "firing"
    }

    return null
  }

  private updateFiring(
    context: EnemyStateContext<NeonMoaiPhase>,
  ): NeonMoaiPhase | null {
    this.applyMoaiFloat(context)
    context.enemyData.vx = context.scrollCompensation - 0.15

    if (context.enemyData.lastShot < context.enemyData.shootCooldown * 0.2) {
      return "recovering"
    }

    return null
  }

  private updateRecovering(
    context: EnemyStateContext<NeonMoaiPhase>,
  ): NeonMoaiPhase | null {
    this.applyMoaiFloat(context)
    context.enemyData.vx = context.scrollCompensation + 0.35
    context.sprite.angle *= 0.85

    if (context.stateTime >= MOAI_RECOVER_MS) {
      return "charging"
    }

    return null
  }

  private applyMoaiFloat(context: EnemyStateContext<NeonMoaiPhase>): void {
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "moai-drift") return

    const homeY = context.enemyData.homeY ?? context.sprite.y
    const bob = Math.sin(context.time / 1000) * spec.amplitudeY
    const playerBias = Phaser.Math.Clamp(
      (context.playerY - context.sprite.y) * 0.01,
      -0.5,
      0.5,
    )
    context.enemyData.vy = (homeY + bob - context.sprite.y) * 0.05 + playerBias
    context.sprite.y = Phaser.Math.Clamp(context.sprite.y, 64, GAME_HEIGHT - 96)
  }
}
